/**
 * Consultas y mutaciones de la API, centralizadas.
 *
 * Todas las mutaciones críticas pasan por el outbox cuando no hay red (ADR-0007): el
 * `Idempotency-Key` se genera acá y viaja igual en el reintento.
 */
import { useMutation, useQuery, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { api, ApiProblem, OfflineError, newIdempotencyKey } from './api.js';
import { enqueue } from './outbox.js';
import type {
  AuditListResponse,
  Bet,
  BetsListResponse,
  CreateBetBody,
  CreateBetResponse,
  CreateFightBody,
  CurrentFightResponse,
  Fight,
  FightResponse,
  HouseMetricsResponse,
  PayBetResponse,
  PendingPayoutsResponse,
  PresetsResponse,
  SettleAllPendingBody,
  SettleAllResponse,
  SettleFightBody,
  SettleResponse,
  TerminalsResponse,
  UpdateUserBody,
  UserResponse,
  UsersListResponse,
  HistoryResponse,
  PublicFightResponse,
  PublicTicketsResponse,
  ExportResponse,
} from '@thepit/contracts';

/* ------------------------- Vista pública (sin sesión) ---------------------- */

export const queryKeys = {
  publicFight: ['public', 'fight'] as const,
  publicTickets: ['public', 'tickets'] as const,
  currentFight: ['fight', 'current'] as const,
  fight: (id: string) => ['fight', id] as const,
  bets: (fightId: string) => ['bets', fightId] as const,
  myBets: ['bets', 'mine'] as const,
  pending: (fightId?: string | null) => ['payouts', 'pending', fightId ?? 'all'] as const,
  presets: ['presets'] as const,
  users: ['users'] as const,
  audit: ['audit'] as const,
  metrics: ['metrics', 'house'] as const,
  history: ['history'] as const,
  terminals: ['terminals'] as const,
};

export function usePublicFight(enabled = true) {
  return useQuery({
    queryKey: queryKeys.publicFight,
    queryFn: () => api.get<PublicFightResponse>('/api/v1/public/fight'),
    enabled,
    refetchOnWindowFocus: true,
  });
}

export function usePublicTickets(enabled = true) {
  return useQuery({
    queryKey: queryKeys.publicTickets,
    queryFn: () => api.get<PublicTicketsResponse>('/api/v1/public/tickets?limit=10'),
    enabled,
  });
}

/* ------------------------------- Consultas -------------------------------- */

/**
 * Polling con freno de costos (ADR-0011).
 *
 * Una pestaña olvidada en segundo plano seguía pidiendo cada 1,5 s: eso despierta la
 * máquina de Fly y la computa de Neon toda la noche. Con el documento oculto se corta.
 */
function whenVisible(ms: number) {
  return (): number | false => {
    if (typeof document !== 'undefined' && document.hidden) return false;
    return ms;
  };
}

export function useCurrentFight(enabled = true) {
  return useQuery({
    queryKey: queryKeys.currentFight,
    queryFn: () => api.get<CurrentFightResponse>('/api/v1/fights/current'),
    enabled,
    refetchInterval: (query) => {
      if (typeof document !== 'undefined' && document.hidden) return false;
      return query.state.data?.fight ? 1500 : 30_000;
    },
  });
}

export function useBets(fightId: string | null) {
  return useQuery({
    queryKey: queryKeys.bets(fightId ?? 'none'),
    queryFn: () => api.get<BetsListResponse>(`/api/v1/fights/${fightId}/bets?limit=200`),
    enabled: Boolean(fightId),
    refetchInterval: whenVisible(1500),
  });
}

export function useMyBets(enabled = true) {
  return useQuery({
    queryKey: queryKeys.myBets,
    queryFn: () => api.get<BetsListResponse>('/api/v1/bets/mine?limit=100'),
    enabled,
  });
}

export function usePendingPayouts(fightId?: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.pending(fightId),
    queryFn: () =>
      api.get<PendingPayoutsResponse>(
        `/api/v1/payouts/pending${fightId ? `?fightId=${fightId}` : ''}`,
      ),
    enabled,
  });
}

export function usePresets(enabled = true) {
  return useQuery({
    queryKey: queryKeys.presets,
    queryFn: () => api.get<PresetsResponse>('/api/v1/presets'),
    enabled,
  });
}

export function useUsers(enabled = true) {
  return useQuery({
    queryKey: queryKeys.users,
    queryFn: () => api.get<UsersListResponse>('/api/v1/users?limit=200'),
    enabled,
  });
}

export function useAudit(enabled = true) {
  return useQuery({
    queryKey: queryKeys.audit,
    queryFn: () => api.get<AuditListResponse>('/api/v1/audit?limit=100'),
    enabled,
  });
}

export function useHouseMetrics(enabled = true) {
  return useQuery({
    queryKey: queryKeys.metrics,
    queryFn: () => api.get<HouseMetricsResponse>('/api/v1/metrics/house'),
    enabled,
  });
}

export function useHistory(enabled = true) {
  return useQuery({
    queryKey: queryKeys.history,
    queryFn: () => api.get<HistoryResponse>('/api/v1/history/fights?limit=50'),
    enabled,
  });
}

export function useTerminals(enabled = true) {
  return useQuery({
    queryKey: queryKeys.terminals,
    queryFn: () => api.get<TerminalsResponse>('/api/v1/terminals'),
    enabled,
    refetchInterval: whenVisible(30_000),
  });
}

export function useBetLookup(query: string, status: 'any' | 'payable' | 'open' = 'any') {
  return useQuery({
    queryKey: ['bets', 'lookup', query, status],
    queryFn: () => api.get<BetsListResponse>(`/api/v1/bets/lookup?q=${encodeURIComponent(query)}&status=${status}`),
    enabled: query.trim().length >= 3,
  });
}

/* ------------------------------- Mutaciones -------------------------------- */

/**
 * Invalidación QUIRÚRGICA: cada mutación refresca solo lo que cambió.
 *
 * Antes se invalidaban todas las familias y se forzaba refetch de fight y bets en cada
 * acción: en la mesa (donde se emite un boleto cada pocos segundos) eso significaba
 * 6+ peticiones por clic, varias repitiendo lo mismo. Con la API suspendida cada
 * petición paga el arranque en frío, así que la medida se siente.
 */
function invalidateFight(client: ReturnType<typeof useQueryClient>): void {
  void client.invalidateQueries({ queryKey: queryKeys.currentFight });
  void client.invalidateQueries({ queryKey: queryKeys.publicFight });
}

function invalidateBets(client: ReturnType<typeof useQueryClient>): void {
  void client.invalidateQueries({ queryKey: ['bets'] });
  void client.invalidateQueries({ queryKey: queryKeys.publicTickets });
  void client.invalidateQueries({ queryKey: queryKeys.myBets });
}

function invalidateMoney(client: ReturnType<typeof useQueryClient>): void {
  void client.invalidateQueries({ queryKey: ['payouts'] });
  void client.invalidateQueries({ queryKey: ['metrics', 'house'] });
}

/** Ejecuta una mutación; si no hay red, la encola en el outbox con su clave. */
async function withOfflineFallback<T>(
  run: (idempotencyKey: string) => Promise<T>,
  fallback: { path: string; method: 'POST' | 'PATCH' | 'PUT'; body: unknown; label: string },
): Promise<T | { queued: true; id: string }> {
  const key = newIdempotencyKey();
  try {
    return await run(key);
  } catch (error) {
    if (error instanceof OfflineError) {
      const entry = await enqueue({ ...fallback, method: fallback.method });
      return { queued: true, id: entry.id };
    }
    throw error;
  }
}

export function useCreateFight() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateFightBody) =>
      withOfflineFallback(
        (key) => api.post<FightResponse>('/api/v1/fights', body, { idempotencyKey: key }),
        { path: '/api/v1/fights', method: 'POST', body, label: 'Pelea nueva' },
      ),
    onSuccess: () => { invalidateFight(client); },
  });
}

export function useLockFight() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; version: number }) =>
      api.post<FightResponse>(`/api/v1/fights/${input.id}/lock`, { version: input.version }),
    onSuccess: () => { invalidateFight(client); invalidateBets(client); },
  });
}

export function useSettleFight() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string } & SettleFightBody) =>
      withOfflineFallback(
        (key) =>
          api.post<SettleResponse>(
            `/api/v1/fights/${input.id}/settle`,
            { version: input.version, winner: input.winner },
            { idempotencyKey: key },
          ),
        {
          path: `/api/v1/fights/${input.id}/settle`,
          method: 'POST',
          body: { version: input.version, winner: input.winner },
          label: 'Liquidación',
        },
      ),
    onSuccess: () => { invalidateFight(client); invalidateBets(client); invalidateMoney(client); },
  });
}

export function useCancelFight() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; version: number; reason: string }) =>
      withOfflineFallback(
        (key) =>
          api.post<FightResponse>(
            `/api/v1/fights/${input.id}/cancel`,
            { version: input.version, reason: input.reason },
            { idempotencyKey: key },
          ),
        {
          path: `/api/v1/fights/${input.id}/cancel`,
          method: 'POST',
          body: { version: input.version, reason: input.reason },
          label: 'Cancelación',
        },
      ),
    onSuccess: () => { invalidateFight(client); invalidateBets(client); invalidateMoney(client); },
  });
}

export function useCreateBet(): UseMutationResult<
  CreateBetResponse | { queued: true; id: string },
  Error,
  { fightId: string; body: CreateBetBody }
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ fightId, body }) =>
      withOfflineFallback(
        (key) => api.post<CreateBetResponse>(`/api/v1/fights/${fightId}/bets`, body, { idempotencyKey: key }),
        { path: `/api/v1/fights/${fightId}/bets`, method: 'POST', body, label: `Boleto ${body.citizenName ?? ''}` },
      ),
    onSuccess: () => { invalidateFight(client); invalidateBets(client); },
  });
}

export function usePayBet() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { betId: string; terminalId?: string }) =>
      withOfflineFallback(
        (key) =>
          api.post<PayBetResponse>(
            `/api/v1/bets/${input.betId}/pay`,
            { terminalId: input.terminalId },
            { idempotencyKey: key },
          ),
        {
          path: `/api/v1/bets/${input.betId}/pay`,
          method: 'POST',
          body: { terminalId: input.terminalId },
          label: 'Pago',
        },
      ),
    onSuccess: () => { invalidateMoney(client); invalidateBets(client); },
  });
}

export function useSettleAll() {
  const client = useQueryClient();
  return useMutation({
    // Pago masivo GLOBAL: la caja siempre ve TODOS los pendientes (`listPending` no filtra
    // por pelea), así que el botón tiene que poder cancelarlos todos, no solo los de una.
    mutationFn: (input: { body: SettleAllPendingBody }) =>
      api.post<SettleAllResponse>('/api/v1/payouts/settle-all', input.body, {
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: () => { invalidateMoney(client); invalidateBets(client); },
  });
}

export function useUpdateUser() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; body: UpdateUserBody }) =>
      api.patch<UserResponse>(`/api/v1/users/${input.id}`, input.body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.users });
      void client.invalidateQueries({ queryKey: queryKeys.audit });
    },
  });
}

export function useApprovePreset() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; to: string; identificacion?: unknown }) =>
      api.post(`/api/v1/presets/${input.id}/approve`, { to: input.to, identificacion: input.identificacion }),
    onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.presets }),
  });
}

export function useHeartbeat() {
  return useMutation({
    mutationFn: (input: { terminalId: string; label: string; kind: string }) =>
      api.post<TerminalsResponse>('/api/v1/terminals/heartbeat', input),
  });
}

export function useExport() {
  return useMutation({
    mutationFn: (input: { fightId: string; format: 'txt' | 'json' | 'csv' }) =>
      api.get<ExportResponse>(`/api/v1/exports/fight/${input.fightId}?format=${input.format}`),
  });
}

export { ApiProblem };
export type { Bet, Fight };
